'use client';

import { useRouter } from 'next/navigation';

import { useTranslations } from 'next-intl';
import { useState, useTransition } from 'react';

import clientConfig from '@/lib/config/client-config';

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

// Frontière d'erreur du groupe (new) — couvre la home et les pages publiques.
// IMPORTANT : le groupe (new) ne charge PAS Tailwind (contrairement à (legacy)), donc les
// classes utilitaires et le composant <Button> n'y ont aucun effet. On utilise donc des
// styles inline avec la palette Sowefund pour garantir un rendu propre quoi qu'il arrive.
// NB : en dev, l'overlay d'erreur de Next s'affiche par-dessus — page surtout visible en prod.
const NAVY = '#021332';
const BLUE = '#0075ff';
const LIGHT_BLUE = '#eaf2ff';
const GREY = '#818b92';

const Error = ({ reset, error }: ErrorProps) => {
  const t = useTranslations();
  const router = useRouter();
  const [seeDetails, setSeeDetails] = useState<boolean>(false);
  // isPending reste vrai pendant la tentative (round-trip serveur du refresh) -> spinner.
  const [isPending, startTransition] = useTransition();

  // reset() seul ne relance pas le fetch serveur qui a échoué : il faut aussi rafraîchir la
  // route (re-render serveur) pour réellement retenter la récupération des données.
  const retry = () => {
    startTransition(() => {
      router.refresh();
      reset();
    });
  };

  return (
    <div
      style={{
        minHeight: '70vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '460px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '18px',
          padding: '40px',
          textAlign: 'center',
          background: '#fff',
          border: '1px solid #e6e9f0',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(2, 19, 50, 0.08)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '64px',
            height: '64px',
            borderRadius: '9999px',
            background: LIGHT_BLUE,
          }}
        >
          <svg
            width="30"
            height="30"
            viewBox="0 0 24 24"
            fill="none"
            stroke={BLUE}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>

        <h2
          style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: NAVY }}
        >
          {t('errorBoundary.unavailableTitle')}
        </h2>

        <p
          style={{ margin: 0, fontSize: '15px', lineHeight: 1.5, color: GREY }}
        >
          {t('errorBoundary.comeBackLater')}
        </p>

        <button
          type="button"
          onClick={retry}
          disabled={isPending}
          style={{
            marginTop: '4px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            padding: '12px 28px',
            fontSize: '15px',
            fontWeight: 600,
            color: '#fff',
            background: BLUE,
            border: 'none',
            borderRadius: '10px',
            cursor: isPending ? 'default' : 'pointer',
            opacity: isPending ? 0.75 : 1,
          }}
        >
          {isPending ? (
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <circle
                cx="12"
                cy="12"
                r="9"
                fill="none"
                stroke="#fff"
                strokeWidth="3"
                strokeOpacity="0.3"
              />
              <path
                d="M12 3a9 9 0 0 1 9 9"
                fill="none"
                stroke="#fff"
                strokeWidth="3"
                strokeLinecap="round"
              >
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 12 12"
                  to="360 12 12"
                  dur="0.8s"
                  repeatCount="indefinite"
                />
              </path>
            </svg>
          ) : null}
          {t('errorBoundary.tryAgain')}
        </button>

        {!clientConfig.isDeployedEnv ? (
          <div style={{ width: '100%', marginTop: '8px' }}>
            <button
              type="button"
              onClick={() => setSeeDetails((value) => !value)}
              style={{
                background: 'none',
                border: 'none',
                color: GREY,
                fontSize: '12px',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              {seeDetails
                ? t('errorBoundary.hideDetails')
                : t('errorBoundary.seeDetails')}
            </button>

            {seeDetails ? (
              <pre
                style={{
                  marginTop: '12px',
                  maxHeight: '220px',
                  overflow: 'auto',
                  textAlign: 'left',
                  fontSize: '11px',
                  lineHeight: 1.5,
                  color: '#c0392b',
                  background: 'rgba(192, 57, 43, 0.06)',
                  border: '1px solid rgba(192, 57, 43, 0.3)',
                  borderRadius: '8px',
                  padding: '12px',
                }}
              >
                {error.stack}
              </pre>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Error;
